import { useState, useRef, useEffect } from "react";
import { Bug, Shield, Phone, Mail, MapPin, Check, Rat, SprayCan, Clock, Calculator, RotateCcw, ChevronRight, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Header, Logo } from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { usePestPricing } from "../hooks/usePestPricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO Component
const PageSEO = () => (
  <>
    <title>General Pest Control Ipswich & Brisbane | Cockroaches, Ants, Spiders</title>
    <meta name="description" content="Professional pest control in Ipswich & Brisbane. Treatment for cockroaches, ants, spiders, bed bugs, rodents & mosquitoes. Safe for pets & children." />
    <meta name="keywords" content="pest control Brisbane, cockroach control Ipswich, ant treatment Brisbane, spider control, bed bug treatment, rodent control Brisbane, mosquito fogging Queensland" />
    <link rel="canonical" href="https://pestfreebrisbane.com.au/pests" />
    <meta property="og:title" content="General Pest Control Ipswich & Brisbane | Pest Free Ipswich & Brisbane" />
    <meta property="og:description" content="Professional pest control for cockroaches, ants, spiders, bed bugs, and more. Call (07) 3708 1422." />
    <meta property="og:url" content="https://pestfreebrisbane.com.au/pests" />
  </>
);

// Theme colors for pests - Red
const theme = {
  primary: "#E74C3C",
  primaryDark: "#C0392B",
  primaryLight: "#E74C3C20",
  gradient: "from-[#E74C3C] to-[#922B21]"
};

export default function PestsPage() {
  const bookingRef = useRef(null);
  const calculatorRef = useRef(null);
  
  const scrollToBooking = () => {
    bookingRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const pestTypes = [
    { name: "Cockroaches", desc: "German, American, and Australian species" },
    { name: "Ants", desc: "Black ants, coastal brown ants, fire ants" },
    { name: "Spiders", desc: "Redbacks, huntsmans, white-tails" },
    { name: "Bed Bugs", desc: "Complete elimination and prevention" },
    { name: "Bird Mites", desc: "Common from bird nesting issues" },
    { name: "Rodents", desc: "Rats and mice control" },
    { name: "Silverfish", desc: "Protect your books and fabrics" },
    { name: "Fleas", desc: "Pet and home flea treatments" },
    { name: "Mosquitoes", desc: "Fogging with professional equipment" },
    { name: "Wasps & Bees", desc: "Safe nest removal" }
  ];

  return (
    <div className="min-h-screen">
      <PageSEO />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="pests-hero">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_fd293a20-78a6-4d86-9d01-01576b2818e2/artifacts/rahuvmhg_IMG_7747.jpeg" alt="" className="w-full h-full object-cover" style={{ objectPosition: '50% 30%' }} />
          <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-75`} />
        </div>
        <div className="absolute inset-0 bg-slate-900/20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Bug className="w-5 h-5" />
                <span className="font-medium">Complete Pest Control</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
                General Pest Control <span className="text-yellow-300">Ipswich & Brisbane</span>
              </h1>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                From cockroaches to bed bugs, rodents to mosquitoes — we handle all pests with professional-grade treatments and equipment.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={scrollToCalculator}
                  className="group relative overflow-hidden rounded-full px-8 py-4 font-semibold text-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
                  style={{ background: 'linear-gradient(90deg, #7ABC32, #5fa828)' }}
                  data-testid="pests-hero-cta"
                >
                  <span className="relative flex items-center justify-center gap-2 text-white">
                    <Calculator className="w-5 h-5" />
                    Price Calculator
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </button>
                <a 
                  href="tel:0737081422"
                  className="group relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300 hover:scale-105 bg-transparent border-2 border-white/50 hover:border-white text-white backdrop-blur-sm"
                >
                  <Phone className="w-5 h-5" />
                  (07) 3708 1422
                </a>
              </div>
            </div>
            
            {/* Team Photo */}
            <div className="hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_3487a350-8ab4-4ea9-a426-6bca9ba7d4b0/artifacts/mcwb6vp8_IMG_7747.jpg"
                  alt="Pest Free Ipswich & Brisbane team with work vehicle"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-slate-900/70 backdrop-blur-sm rounded-lg px-4 py-3">
                  <p className="text-white font-medium">Professional pest control with industrial-grade equipment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Pest Types Section */}
      <section className="py-20 bg-white" data-testid="pests-types">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
              Pests We <span style={{ color: theme.primary }}>Eliminate</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We handle all common household and commercial pests with effective, safe treatments.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {pestTypes.map((pest, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-100 hover:border-[#E74C3C]/30 hover:shadow-md transition-all">
                <h3 className="font-semibold text-slate-900 mb-1">{pest.name}</h3>
                <p className="text-sm text-slate-500">{pest.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Services Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Professional <span className="text-[#E74C3C]">Treatment Methods</span>
            </h2>
            <p className="text-lg text-slate-400">Industry-leading equipment and techniques</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: SprayCan, 
                title: "Internal Spray", 
                desc: "Behind appliances, cupboards, and hidden areas where pests hide" 
              },
              { 
                icon: Shield, 
                title: "Roof Void Treatment", 
                desc: "Dusting and treatment of roof spaces and weep holes" 
              },
              { 
                icon: Bug, 
                title: "Perimeter Barrier", 
                desc: "External spray creating a protective barrier around your home" 
              },
              { 
                icon: SprayCan, 
                title: "Mosquito Fogging", 
                desc: "Professional fogging with our $5k commercial unit" 
              }
            ].map((service, i) => (
              <div key={i} className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="inline-flex p-3 bg-[#E74C3C]/20 rounded-xl mb-4">
                  <service.icon className="w-8 h-8 text-[#E74C3C]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-slate-400">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Pricing Calculator Section */}
      <div ref={calculatorRef}>
        <PricingCalculator theme={theme} />
      </div>
      
      {/* Gallery Placeholder */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Our Work in <span style={{ color: theme.primary }}>Action</span>
            </h2>
            <p className="text-lg text-slate-600">Professional pest treatments across Ipswich & Brisbane</p>
          </div>
          
          {/* Placeholder for gallery - will be updated with real photos/videos */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Behind dishwasher treatment",
              "Roof void dusting",
              "Mosquito fogging"
            ].map((title, i) => (
              <div key={i} className="aspect-video bg-slate-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-300">
                <div className="text-center text-slate-400 p-4">
                  <SprayCan className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm font-medium">{title}</p>
                  <p className="text-xs">Video coming soon</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
              Pest Control Across <span style={{ color: theme.primary }}>Ipswich & Brisbane</span>
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {["Brisbane", "Ipswich", "Springfield", "Logan", "Redbank Plains", "Indooroopilly", "Carindale", "Western Suburbs"].map((area, i) => (
              <span key={i} className="px-4 py-2 bg-slate-100 rounded-full text-slate-700 font-medium">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// Pricing Calculator Component
function PricingCalculator({ theme }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [propertyType, setPropertyType] = useState("house");
  const [bedrooms, setBedrooms] = useState("3");
  const [storeys, setStoreys] = useState("single");
  const [treatmentType, setTreatmentType] = useState("standard");
  const [serviceFrequency, setServiceFrequency] = useState("oneoff");
  const [rodentTreatment, setRodentTreatment] = useState(false);
  const [termiteAddOn, setTermiteAddOn] = useState(false);
  
  // Contact form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [message, setMessage] = useState("");
  
  const { prices, loading } = usePestPricing({
    propertyType, bedrooms, storeys, treatmentType, serviceFrequency, rodentTreatment, termiteAddOn,
  });
  
  // Calculate launch discount (10% off total per visit price)
  const launchDiscount = Math.round(prices.pricePerVisit * 0.10);
  const discountedPrice = prices.pricePerVisit - launchDiscount;
  
  // Sticky mobile price bar - show only during calculator section, hide at final price
  const priceBoxRef = useRef(null);
  const calcSectionRef = useRef(null);
  const [showStickyPrice, setShowStickyPrice] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const calc = calcSectionRef.current;
      const price = priceBoxRef.current;
      if (!calc) return;
      const calcRect = calc.getBoundingClientRect();
      const inCalc = calcRect.top < window.innerHeight && calcRect.bottom > 100;
      let priceVis = false;
      if (price) {
        const pr = price.getBoundingClientRect();
        priceVis = pr.top < window.innerHeight && pr.bottom > 0;
      }
      setShowStickyPrice(inCalc && !priceVis);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleReset = () => {
    setPropertyType("house");
    setBedrooms("3");
    setStoreys("single");
    setTreatmentType("standard");
    setServiceFrequency("oneoff");
    setRodentTreatment(false);
    setTermiteAddOn(false);
    setName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setMessage("");
    setSubmitted(false);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/pest-estimate`, {
        name,
        email,
        phone,
        address,
        propertyType,
        bedrooms: parseInt(bedrooms),
        storeys,
        treatmentType,
        serviceFrequency,
        rodentTreatment,
        rodentPrice: prices.rodentPrice || null,
        rodentStations: rodentTreatment ? (treatmentType === "comprehensive" ? 6 : 3) : null,
        rodentWarranty: rodentTreatment ? (treatmentType === "comprehensive" ? "6 months" : "3 months") : null,
        termiteAddOn,
        pricePerVisit: prices.pricePerVisit,
        annualTotal: prices.annualTotal || null,
        savings: prices.savings || null,
        termiteInspectionEstimate: prices.termiteInspection || null,
        termiteBarrierEstimate: prices.termiteBarrier || null,
        termiteBarrierDiscounted: prices.termiteBarrierDiscounted || null,
        message,
        timestamp: new Date().toISOString()
      });
      
      setSubmitted(true);
      toast.success("Estimate request sent! We'll be in touch shortly.");
    } catch (error) {
      console.error("Failed to submit estimate:", error);
      toast.error("Failed to submit. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Refs for auto-scroll between questions
  const refBedrooms = useRef(null);
  const refStoreys = useRef(null);
  const refTreatment = useRef(null);
  const refFrequency = useRef(null);
  const refAddons = useRef(null);
  const scrollTo = (ref) => setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);

  if (loading) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="animate-pulse">Loading calculator...</div>
        </div>
      </section>
    );
  }

  return (
    <section ref={calcSectionRef} className="py-20 bg-slate-50" data-testid="pest-calculator">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#E74C3C]/10 rounded-full px-4 py-2 mb-4">
            <Calculator className="w-5 h-5 text-[#E74C3C]" />
            <span className="font-medium text-[#E74C3C]">Get Your Price in 60 Seconds</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-slate-900 mb-4">
            General Pest <span style={{ color: theme.primary }}>Pricing Calculator</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Get an instant estimate for your property. Adjust the options below to see pricing.
          </p>
        </div>
        
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="grid lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
            {/* Left Column - Calculator Options */}
            <div className="lg:col-span-3 p-6 md:p-8">
              <div className="space-y-8">
                {/* Property Type */}
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Property Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "house", label: "House" },
                      { value: "unit", label: "Unit / Townhouse" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => { setPropertyType(option.value); scrollTo(refBedrooms); }}
                        className={`p-4 rounded-xl border-2 text-center font-medium transition-all ${
                          propertyType === option.value
                            ? "border-[#7ABC32] bg-[#7ABC32]/5 text-[#7ABC32]"
                            : "border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Bedrooms */}
                <div ref={refBedrooms} className="scroll-mt-20">
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Number of Bedrooms</Label>
                  <div className="grid grid-cols-6 gap-2">
                    {["1", "2", "3", "4", "5", "6"].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => { setBedrooms(num); scrollTo(refStoreys); }}
                        className={`p-3 rounded-xl border-2 text-center font-semibold transition-all ${
                          bedrooms === num
                            ? "border-[#7ABC32] bg-[#7ABC32]/5 text-[#7ABC32]"
                            : "border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}
                      >
                        {num === "6" ? "6+" : num}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Storeys */}
                <div ref={refStoreys} className="scroll-mt-20">
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">House Height</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "single", label: "Single Storey" },
                      { value: "double", label: "Double Storey" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => { setStoreys(option.value); scrollTo(refTreatment); }}
                        className={`p-4 rounded-xl border-2 text-center font-medium transition-all ${
                          storeys === option.value
                            ? "border-[#7ABC32] bg-[#7ABC32]/5 text-[#7ABC32]"
                            : "border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Treatment Type - BEFORE Frequency */}
                <div ref={refTreatment} className="scroll-mt-20">
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Treatment Type</Label>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => { setTreatmentType("standard"); scrollTo(refFrequency); }}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        treatmentType === "standard"
                          ? "border-[#7ABC32] bg-[#7ABC32]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">Standard Treatment</span>
                        {treatmentType === "standard" && <Check className="w-5 h-5 text-[#7ABC32]" />}
                      </div>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> Internal treatment</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> External treatment</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> Harbourage areas (behind fridge, dishwasher, under sinks, subfloor)</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> Cockroach gel bait application (kitchen, bathrooms and infested areas)</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> <span className="text-[#7ABC32] font-medium">3 month warranty</span></li>
                      </ul>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => { setTreatmentType("comprehensive"); scrollTo(refFrequency); }}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        treatmentType === "comprehensive"
                          ? "border-[#7ABC32] bg-[#7ABC32]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">Comprehensive Treatment</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-[#7ABC32] text-white px-2 py-1 rounded-full">Recommended</span>
                          {treatmentType === "comprehensive" && <Check className="w-5 h-5 text-[#7ABC32]" />}
                        </div>
                      </div>
                      <ul className="text-sm text-slate-600 space-y-1 mb-3">
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Internal treatment</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> External treatment</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Harbourage areas (behind fridge, dishwasher, under sinks, subfloor)</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Cockroach gel bait application (kitchen, bathrooms and infested areas)</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Fence / full perimeter treatment</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Roof void dusting</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> 3x cockroach pheromone sticky traps</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Ant treatment included</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> Multiple professional-grade products used</li>
                        <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#7ABC32] mt-0.5 shrink-0" /> <span className="text-[#7ABC32] font-medium">6 month warranty</span></li>
                      </ul>
                      <p className="text-xs text-[#7ABC32] italic">
                        Deeper coverage and longer-lasting protection by targeting hidden harbourage zones, roof void activity, fence lines and ants to help prevent reinfestation.
                      </p>
                    </button>
                  </div>
                </div>

                {/* Service Frequency */}
                <div ref={refFrequency} className="scroll-mt-20">
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Service Frequency</Label>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => { setServiceFrequency("sixmonthly"); scrollTo(refAddons); }}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        serviceFrequency === "sixmonthly"
                          ? "border-[#7ABC32] bg-[#7ABC32]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">6-Monthly Protection Plan</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-[#7ABC32] text-white px-2 py-1 rounded-full">Recommended</span>
                          {serviceFrequency === "sixmonthly" && <Check className="w-5 h-5 text-[#7ABC32]" />}
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">
                        Preventative maintenance every 6 months reduces infestations before they start and costs less per visit than yearly treatments.
                      </p>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => { setServiceFrequency("oneoff"); scrollTo(refAddons); }}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        serviceFrequency === "oneoff"
                          ? "border-[#7ABC32] bg-[#7ABC32]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-slate-900">Yearly Visits</span>
                        {serviceFrequency === "oneoff" && <Check className="w-5 h-5 text-[#7ABC32]" />}
                      </div>
                      <p className="text-sm text-slate-500">
                        Single annual treatment. Note: This option is less cost-effective than the 6-monthly plan and may require more reactive treatments if infestations occur between visits.
                      </p>
                    </button>
                  </div>
                </div>
                
                {/* Add-ons */}
                <div ref={refAddons} className="scroll-mt-20">
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Optional Add-Ons</Label>
                  <div className="space-y-3">
                    <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      rodentTreatment ? "border-[#7ABC32] bg-[#7ABC32]/5" : "border-slate-200 hover:border-slate-300"
                    }`}>
                      <Checkbox
                        checked={rodentTreatment}
                        onCheckedChange={setRodentTreatment}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900">Rodent Treatment Add-On</span>
                          {rodentTreatment && (
                            <span className="text-sm font-semibold text-[#E74C3C]">
                              +${(() => {
                                // Base price $175, adjust for size
                                let rodentPrice = 175;
                                const bedroomNum = parseInt(bedrooms);
                                // Smaller houses get discount
                                if (bedroomNum <= 2) rodentPrice -= 25;
                                else if (bedroomNum >= 5) rodentPrice += 25;
                                // Height premium
                                if (storeys === "double") rodentPrice += 20;
                                // Comprehensive gets more stations so slightly more expensive
                                if (treatmentType === "comprehensive") rodentPrice += 35;
                                return rodentPrice;
                              })()}
                            </span>
                          )}
                        </div>
                        {rodentTreatment && (
                          <div className="mt-2 text-sm">
                            <p className="text-slate-600">
                              {treatmentType === "comprehensive" 
                                ? "Inspection and installation of up to 6 tamper proof, lockable rodent bait stations included with your comprehensive treatment"
                                : "Inspection and installation of up to 3 tamper proof, lockable rodent bait stations included with your standard treatment"
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    </label>
                    
                    <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      termiteAddOn ? "border-[#D2691E] bg-[#D2691E]/5" : "border-slate-200 hover:border-slate-300"
                    }`}>
                      <Checkbox
                        checked={termiteAddOn}
                        onCheckedChange={setTermiteAddOn}
                        className="mt-1"
                      />
                      <div>
                        <span className="font-medium text-slate-900">Add Yearly Termite Inspection</span>
                        {termiteAddOn && (
                          <div className="mt-2 text-sm">
                            <p className="text-slate-600">
                              Annual termite inspection to detect any termite activity early and protect your property.
                            </p>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Price Display & Form */}
            <div className="lg:col-span-2 bg-slate-50 p-6 md:p-8">
              <div className="sticky top-24">
                {/* Unified Pricing Box with Logo */}
                <div ref={priceBoxRef} className="bg-[#E74C3C] rounded-2xl overflow-hidden mb-6">
                  {/* Header with Logo and Promo */}
                  <div className="bg-[#c0392b] px-5 py-3 flex items-center justify-between">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png"
                      alt="Pest Free"
                      className="h-10"
                    />
                    <span className="text-amber-400 text-sm font-bold whitespace-nowrap">🎉 10% OFF LAUNCH SPECIAL</span>
                  </div>
                  
                  {/* Main Price */}
                  <div className="p-5 text-white">
                    <p className="text-sm text-white/80 mb-1">Your Estimated Price</p>
                    <div className="flex items-baseline gap-3">
                      <span className="text-xl text-white/50 line-through">${prices.pricePerVisit}</span>
                      <span className="text-4xl font-bold">${discountedPrice}</span>
                      <span className="text-sm text-white/70">per visit</span>
                    </div>
                    <p className="text-amber-400 font-semibold text-sm mt-1">You save ${launchDiscount} (10% off)</p>
                    <p className="text-xs text-yellow-300 font-semibold mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>
                    
                    {/* Add-ons Summary */}
                    {(rodentTreatment || termiteAddOn || serviceFrequency === "sixmonthly") && (
                      <div className="mt-4 pt-4 border-t border-white/20 space-y-2">
                        {rodentTreatment && prices.rodentPrice > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-white/80">+ Rodent Treatment</span>
                            <span className="font-medium">${prices.rodentPrice}</span>
                          </div>
                        )}
                        
                        {termiteAddOn && (
                          <div className="flex justify-between text-sm">
                            <span className="text-white/80">+ Termite Inspection <span className="text-[#7ABC32] text-xs">(20% off)</span></span>
                            <span className="font-medium">${prices.termiteInspection}</span>
                          </div>
                        )}
                        
                        {serviceFrequency === "sixmonthly" && (
                          <div className="flex justify-between text-sm text-white/70">
                            <span>Annual (2 visits)</span>
                            <span>${Math.round(prices.annualTotal * 0.9)}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Total if add-ons selected */}
                    {(termiteAddOn || (rodentTreatment && serviceFrequency === "sixmonthly")) && (
                      <div className="mt-3 pt-3 border-t border-white/30 flex justify-between items-baseline">
                        <span className="font-semibold">Total</span>
                        <span className="text-2xl font-bold">
                          ${termiteAddOn 
                            ? discountedPrice + prices.termiteInspection 
                            : Math.round(prices.annualTotal * 0.9)}
                        </span>
                      </div>
                    )}
                    <div className="text-white/50 text-[10px] mt-2">Prices include GST</div>
                  </div>
                </div>
                
                {/* Rodent Notice */}
                {rodentTreatment && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <p className="text-sm text-amber-800">
                      <strong>Rodent Treatment:</strong> We'll provide a custom quote based on your property.
                    </p>
                  </div>
                )}
                
                {/* Contact Form */}
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Full Name *</Label>
                      <Input
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Email *</Label>
                      <Input
                        required
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Phone *</Label>
                      <Input
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="0400 000 000"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Address *</Label>
                      <Textarea
                        required
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="e.g., 123 Example Street, Ipswich QLD 4305"
                        rows={2}
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1 block">Message (optional)</Label>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Any additional details..."
                        rows={3}
                        className="bg-white"
                      />
                    </div>
                    
                    <p className="text-xs text-slate-500 italic">
                      Final pricing subject to inspection and property conditions.
                    </p>
                    
                    <div className="flex gap-3">
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 rounded-xl font-semibold"
                      >
                        {submitting ? "Sending..." : "Get My Quote Now"}
                        <ChevronRight className="w-5 h-5 ml-1" />
                      </Button>
                      <Button
                        type="button"
                        onClick={handleReset}
                        variant="outline"
                        className="px-4 py-6 rounded-xl"
                      >
                        <RotateCcw className="w-5 h-5" />
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Sent!</h3>
                    <p className="text-slate-600 mb-6">We'll be in touch shortly to confirm your quote.</p>
                    <Button onClick={handleReset} variant="outline" className="rounded-xl">
                      <RotateCcw className="w-4 h-4 mr-2" /> Start New Estimate
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Quick Callback Option */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 mb-3">Prefer to talk? We'll call you back within 30 minutes.</p>
          <a 
            href="tel:0737081422"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            <Phone className="w-5 h-5" />
            Call (07) 3708 1422
          </a>
        </div>
      </div>

      {/* Sticky mobile price bar - only during calculator, fades at final price */}
      {showStickyPrice && (
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-[#E74C3C] text-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.15)] transition-opacity duration-300">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <div>
              <span className="text-xs text-white/70">Your estimate</span>
              <div className="flex items-baseline gap-2">
                <span className="text-sm text-white/50 line-through">${prices.pricePerVisit}</span>
                <span className="text-xl font-bold">${discountedPrice}</span>
                <span className="text-xs text-white/60">/visit</span>
              </div>
            </div>
            <span className="text-xs font-semibold text-amber-300 bg-white/10 px-2 py-1 rounded-full">10% off</span>
          </div>
        </div>
      )}
    </section>
  );
}

// Contact Form Component
function ContactForm({ serviceType, theme, pestTypes }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    suburb: "",
    propertyType: "",
    pestTypes: [],
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handlePestTypeChange = (pestName, checked) => {
    if (checked) {
      setFormData({...formData, pestTypes: [...formData.pestTypes, pestName]});
    } else {
      setFormData({...formData, pestTypes: formData.pestTypes.filter(p => p !== pestName)});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        serviceType,
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href
      });
      setSubmitted(true);
      toast.success("Message sent! We'll contact you within 2 hours.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
        <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">Message Received!</h3>
        <p className="text-lg text-slate-600 mb-6">
          Thanks! We'll contact you within 2 hours to discuss your pest problem.
        </p>
        <a 
          href="tel:0737081422"
          className="inline-flex items-center gap-2 text-lg font-semibold hover:underline"
          style={{ color: theme.primary }}
        >
          <Phone className="w-5 h-5" />
          Or call now: (07) 3708 1422
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 border border-slate-200 shadow-sm" data-testid="contact-form">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Full Name *</Label>
          <Input
            required
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full"
            placeholder="Your name"
            data-testid="contact-name"
          />
        </div>
        
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Phone *</Label>
          <Input
            required
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="w-full"
            placeholder="0400 000 000"
            data-testid="contact-phone"
          />
        </div>
        
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Email *</Label>
          <Input
            required
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="w-full"
            placeholder="your@email.com"
            data-testid="contact-email"
          />
        </div>
        
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Suburb *</Label>
          <Input
            required
            value={formData.suburb}
            onChange={(e) => setFormData({...formData, suburb: e.target.value})}
            className="w-full"
            placeholder="e.g., Brisbane, Ipswich"
            data-testid="contact-suburb"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Property Address</Label>
          <Input
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            className="w-full"
            placeholder="Street address (optional)"
            data-testid="contact-address"
          />
        </div>
        
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700 mb-3 block">What pests are you dealing with?</Label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {pestTypes.slice(0, 10).map((pest) => (
              <div key={pest.name} className="flex items-center space-x-2">
                <Checkbox
                  id={pest.name}
                  checked={formData.pestTypes.includes(pest.name)}
                  onCheckedChange={(checked) => handlePestTypeChange(pest.name, checked)}
                />
                <Label htmlFor={pest.name} className="cursor-pointer text-sm">{pest.name}</Label>
              </div>
            ))}
          </div>
        </div>
        
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Tell us more about the problem</Label>
          <Textarea
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            className="w-full min-h-[120px]"
            placeholder="Where are you seeing pests? How long has this been an issue?"
            data-testid="contact-message"
          />
        </div>
      </div>
      
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 text-lg py-6 rounded-full text-white"
          style={{ backgroundColor: theme.primary }}
          data-testid="contact-submit"
        >
          {submitting ? "Sending..." : "Send Message"}
        </Button>
        <a 
          href="tel:0737081422"
          className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-lg font-semibold border-2 transition-all"
          style={{ borderColor: theme.primary, color: theme.primary }}
        >
          <Phone className="w-5 h-5" />
          Call Instead
        </a>
      </div>
    </form>
  );
}